import { CHAT_MAX_LENGTH, type RosterEntry } from '@veriverse/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  loadBlocked,
  loadConversations,
  loadRoster,
  openConversation,
  sendDm,
  setBlocked,
} from '../net/dm.js';
import { useSessionStore } from '../state/session-store.js';
import { formatChatTimestamp } from '../state/office-clock.js';

/** Direct messages: conversation list, one thread at a time, and block/unblock (spec §6.7). */
export function DmPanel() {
  const { t } = useTranslation();
  const open = useSessionStore((s) => s.dmOpen);
  const activeDm = useSessionStore((s) => s.activeDm);
  const [roster, setRoster] = useState<RosterEntry[]>([]);

  useEffect(() => {
    if (!open) return;
    void loadConversations();
    void loadBlocked();
    void loadRoster().then(setRoster, () => undefined);
  }, [open]);

  if (!open) return null;
  return (
    <section className="dm hud-box" aria-label={t('dm.title')}>
      <header className="row between">
        <strong>{t('dm.title')}</strong>
        <button
          type="button"
          className="secondary"
          onClick={() => useSessionStore.getState().setDmOpen(false)}
        >
          {t('common.close')}
        </button>
      </header>
      {activeDm ? <Thread userId={activeDm} /> : <ConversationList roster={roster} />}
    </section>
  );
}

function ConversationList({ roster }: { roster: RosterEntry[] }) {
  const { t } = useTranslation();
  const conversations = useSessionStore((s) => s.conversations);
  const me = useSessionStore((s) => s.user?.id);
  const blocked = useSessionStore((s) => s.blocked);

  return (
    <div className="stack tight">
      <label className="row">
        <span className="sr-only">{t('dm.newConversation')}</span>
        <select
          value=""
          onChange={(event) => event.target.value && void openConversation(event.target.value)}
        >
          <option value="">{t('dm.newConversation')}</option>
          {roster
            .filter((r) => r.userId !== me)
            .map((r) => (
              <option key={r.userId} value={r.userId}>
                {r.displayName}
              </option>
            ))}
        </select>
      </label>
      {conversations.length === 0 && <p className="small">{t('dm.empty')}</p>}
      <ul className="dm-list">
        {conversations.map((c) => (
          <li key={c.userId}>
            <button
              type="button"
              className="secondary"
              onClick={() => void openConversation(c.userId)}
            >
              <span>{c.displayName}</span>
              {c.unread > 0 && !c.blocked && <span className="badge">{c.unread}</span>}
              {c.blocked && <em className="small"> ({t('dm.blockedTag')})</em>}
              <small className="dm-preview">{c.lastBody}</small>
            </button>
          </li>
        ))}
      </ul>
      {blocked.length > 0 && (
        <details>
          <summary className="small">{t('dm.blockedList', { count: blocked.length })}</summary>
          <ul className="dm-list">
            {blocked.map((b) => (
              <li key={b.userId} className="row between">
                <span>{b.displayName}</span>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => void setBlocked(b.userId, false)}
                >
                  {t('dm.unblock')}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Thread({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const messages = useSessionStore((s) => s.threads[userId]);
  const conversation = useSessionStore((s) => s.conversations.find((c) => c.userId === userId));
  const knownName = useSessionStore((s) => s.names[userId]);
  const notice = useSessionStore((s) => s.dmNotice);
  const me = useSessionStore((s) => s.user?.id);
  const [text, setText] = useState('');
  const listRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const name = conversation?.displayName ?? knownName ?? '?';
  const blocked = conversation?.blocked ?? false;

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages?.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    sendDm(userId, text);
    setText('');
    inputRef.current?.blur(); // back to walking; Escape also works while typing
  };

  return (
    <div className="stack tight">
      <div className="row between">
        <button
          type="button"
          className="secondary"
          onClick={() => useSessionStore.getState().setActiveDm(undefined)}
        >
          ←
        </button>
        <strong>{name}</strong>
        <button
          type="button"
          className="secondary"
          onClick={() => void setBlocked(userId, !blocked)}
        >
          {blocked ? t('dm.unblock') : t('dm.block')}
        </button>
      </div>
      <ol ref={listRef} className="chat-log dm-log" aria-live="polite">
        {(messages ?? []).map((m) => (
          <li key={m.id} className={m.senderId === me ? 'mine' : undefined}>
            <time dateTime={new Date(m.at).toISOString()}>
              {formatChatTimestamp(new Date(m.at))}
            </time>{' '}
            <strong>{m.senderId === me ? t('dm.you') : m.senderName}</strong>: {m.body}
          </li>
        ))}
      </ol>
      {notice && (
        <p role="alert" className="error small">
          {t(`dm.notices.${notice}`)}
        </p>
      )}
      <form onSubmit={submit} className="chat-form">
        <input
          ref={inputRef}
          value={text}
          maxLength={CHAT_MAX_LENGTH}
          placeholder={t('dm.placeholder')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') inputRef.current?.blur();
          }}
          disabled={blocked}
          autoComplete="off"
        />
      </form>
      {blocked && <p className="small">{t('dm.blockedNote')}</p>}
    </div>
  );
}
